// Per-investor-type badge and pin theming — Figma InvestorTypeTag (34:3898) and
// Map Pins (231:8430).
//
// The design draws twelve classifications, each with its own glyph, over four
// colour pairs, plus a thirteenth generic variant. The glyph is per type, not
// per colour group: Landlord, Home Builder and Real Estate Investment &
// Development share Orange but draw a key, a pair of figures and a bar chart
// respectively.
//
//   Emerald  #5AC471 on #E5FFEB — Cash Buyer
//   Orange   #F16722 on #FFEBE1 — Landlord, Home Builder, Real Estate Investment & Development
//   Teal     #128292 on #E2F7FA — Flipper, Land Bank, Commercial Land Owner, Trust
//   Gold     #F4AC21 on #FFF9ED — IBuyer, Title Company, Financing, Utility Company
//   Grey     #66707D on #EFF2F6 — "Investor", the generic variant (below)
//
// Emerald is Cash Buyer's alone. The Figma variant that draws it is NAMED
// "Real Estate Investment and Development" — the real REI&D is a second,
// lower-case variant in Orange — so check this map against the design's
// RENDERED LABELS, not its variant names. Reading the names is what previously
// put Flipper, Landlord and IBuyer in Emerald with it (ATAI-479).
//
// The same colour fills the map pin's teardrop and tints its glyph inside the
// white disc, so one entry themes both surfaces.
//
// Keys are the vendor's classification tokens as they arrive from BatchData
// (upper case, space separated).
const EMERALD = { color: '#5ac471', background: '#e5ffeb' };
const ORANGE = { color: '#f16722', background: '#ffebe1' };
const TEAL = { color: '#128292', background: '#e2f7fa' };
const GOLD = { color: '#f4ac21', background: '#fff9ed' };

const THEME = {
  'CASH BUYER': { ...EMERALD, icon: 'iconInvestorTypeCashBuyer', label: 'Cash Buyer' },

  LANDLORD: { ...ORANGE, icon: 'iconInvestorTypeLandlord', label: 'Landlord' },
  'HOME BUILDER': { ...ORANGE, icon: 'iconInvestorTypeHomeBuilder', label: 'Home Builder' },
  'REAL ESTATE INVESTMENT AND DEVELOPMENT': {
    ...ORANGE,
    icon: 'iconInvestorTypeReiDevelopment',
    label: 'Real Estate Investment & Development',
  },

  FLIPPER: { ...TEAL, icon: 'iconInvestorTypeFlipper', label: 'Flipper' },
  'LAND BANK': { ...TEAL, icon: 'iconInvestorTypeLandBank', label: 'Land Bank' },
  'COMMERCIAL LAND OWNER': {
    ...TEAL,
    icon: 'iconInvestorTypeCommercialLandOwner',
    label: 'Commercial Land Owner',
  },
  TRUST: { ...TEAL, icon: 'iconInvestorTypeTrust', label: 'Trust' },
  // Observed in live responses but absent from both the PRD list and the
  // design's variants. Both are a trust in every sense the badge cares about,
  // so they take TRUST's teal and shield rather than reading as unknown types
  // sitting grey next to the TRUST badge the same investor also carries.
  'FAMILY TRUST': { ...TEAL, icon: 'iconInvestorTypeTrust', label: 'Family Trust' },
  'LIVING TRUST': { ...TEAL, icon: 'iconInvestorTypeTrust', label: 'Living Trust' },

  IBUYER: { ...GOLD, icon: 'iconInvestorTypeIBuyer', label: 'IBuyer' },
  'TITLE COMPANY': { ...GOLD, icon: 'iconInvestorTypeTitleCompany', label: 'Title Company' },
  FINANCING: { ...GOLD, icon: 'iconInvestorTypeFinancing', label: 'Financing' },
  'UTILITY COMPANY': { ...GOLD, icon: 'iconInvestorTypeUtilityCompany', label: 'Utility Company' },
};

const entries = (key) =>
  Object.keys(THEME).reduce((out, type) => Object.assign(out, { [type]: THEME[type][key] }), {});

// Sprite ids, rendered through components/base/SVG and tinted by the type colour.
export const InvestorTypeIcons = entries('icon');

// Text colour for the badge, and the colour the map pin is filled with.
export const InvestorTypeColors = entries('color');

// Badge fill. Explicit per the design rather than derived from the text colour:
// the specified fills are not tints of their text colours.
export const InvestorTypeBackgrounds = entries('background');

// The design's own generic variant — "Investor" on the badge (34:3898
// Variant13), "General Investor" on the map pin (231:8430): Grey/700 on Grey/50
// with a briefcase. It is a drawn variant, not an invented fallback, which is
// why unmapped types get a glyph here rather than a bare label.
//
// Everything the design does not draw lands on it: live responses carry BANK,
// SCHOOL and CHURCH, none of which have a variant of their own.
//
// These stay OUT of THEME on purpose. `getPrimaryInvestorType` picks the first
// classification THEME knows, so mapping them would make the alphabetically
// first token win — DR HORTON would pin as BANK instead of LANDLORD.
export const DEFAULT_TYPE_COLOR = '#66707d';
const DEFAULT_TYPE_BACKGROUND = '#eff2f6';
const DEFAULT_TYPE_ICON = 'iconInvestorTypeGeneral';

// The vendor's tokens are not perfectly stable: the design writes "Real Estate
// Investment & Development" while the classification arrives spelled out, and
// separators vary (spaces, underscores, hyphens). Fold all of that to one key so
// a variant spelling still finds its glyph and colour instead of silently
// falling through to the grey default.
const normalize = (type) =>
  String(type || '')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const THEME_BY_KEY = Object.keys(THEME).reduce(
  (out, type) => Object.assign(out, { [normalize(type)]: THEME[type] }),
  {}
);

// Unknown classifications still read as words rather than shouting the raw
// token — BANK renders "Bank", not "BANK".
const titleCase = (type) =>
  normalize(type)
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const getInvestorTypeColor = (type) =>
  (THEME_BY_KEY[normalize(type)] || {}).color || DEFAULT_TYPE_COLOR;

export const getInvestorTypeBackground = (type) =>
  (THEME_BY_KEY[normalize(type)] || {}).background || DEFAULT_TYPE_BACKGROUND;

export const getInvestorTypeIcon = (type) =>
  (THEME_BY_KEY[normalize(type)] || {}).icon || DEFAULT_TYPE_ICON;

// Display text for a classification. The vendor sends upper-case tokens; the
// design draws them title-cased (34:3898), so never render the raw token.
export const getInvestorTypeLabel = (type) =>
  (THEME_BY_KEY[normalize(type)] || {}).label || titleCase(type);

// A map pin carries one glyph, so an investor with several classifications has
// to pick one. The service returns them alphabetically, which puts vendor
// tokens the design does not draw first — DR HORTON leads with BANK — so take
// the first classification the design actually themes, and only fall back to
// the raw first when none of them are known.
export const getPrimaryInvestorType = (classifications) => {
  const list = classifications || [];

  return list.find((type) => Boolean(THEME_BY_KEY[normalize(type)])) || list[0] || null;
};
